import { useif } from "../state/types";
import { addString, Strings } from "./strings";
import { EntityErrors, FieldErrors, isEntityErrorsValid, isFieldErrorsValid, NoEntityError, NoFieldError, WholeEntityError } from "./validatableentity";

type Validator<VT> = (
    value: VT,
    message?: Strings,
    reference?: VT
) => FieldErrors;

const DefaultValidator: Validator<any> =
    (value, reference) => ({ ...NoFieldError });

const validateIntGEZ: Validator<any> = (input: any, message: Strings): FieldErrors => {
    const value = Number(input);
    return ({
        errors: useif(
            isNaN(value) || (value % 1) !== 0 || value < 0,
            message ?? "Očekáváno nezáporné celé číslo"
        ),
    });
}

const validateIntGZ: Validator<any> = (input: any, message: Strings): FieldErrors => {
    const value = Number(input);
    return ({
        errors: useif(
            isNaN(value) || (value % 1) !== 0 || value <= 0,
            message ?? "Očekáváno kladné celé číslo"
        ),
    });
}

const validateInt: Validator<any> = (input: any, message: Strings): FieldErrors => {
    const value = Number(input);
    return ({
        errors: useif(
            isNaN(value) || (value % 1) !== 0,
            message ?? "Očekáváno celé číslo"
        )
    });
}

const validateNumberGEZ: Validator<any> = (input: any, message: Strings): FieldErrors => {
    const value = Number(input);
    return ({
        errors: useif(
            isNaN(value) || value < 0,
            message ?? "Očekáváno nezáporné číslo"
        ),
    });
}

const validateNumberLE: Validator<any> = (input: any, message: Strings, reference: any): FieldErrors => {
    const value = Number(input);
    return ({
        errors: useif(
            isNaN(value) || value > reference,
            message ?? ("Očekáváno číslo menší nebo rovno " + reference)
        ),
    });
}

const validateNumber: Validator<any> = (input: any, message: Strings): FieldErrors => {
    const value = Number(input);
    return ({
        errors: useif(isNaN(value),
            message ?? "Očekáváno číslo"
        ),
    });
}

const validateNotEmpty: Validator<string> = (input: any, message: Strings): FieldErrors => {
    return ({
        errors: useif(
            Array.isArray(input) ?
                input.length === 0 :
                input === null || input === undefined || input === "",
            message ?? "Údaj je vyžadován."
        ),
    });
}

const validateChecked: Validator<boolean> = (input: any, message: Strings): FieldErrors => {
    return ({
        errors: useif(
            input === null || input === undefined || input === false,
            message ?? "Údaj musí být nastaven"
        ),
    });
}

const validateEquals: Validator<any> = (input: any, message: Strings, reference: any): FieldErrors => {
    return ({
        errors: useif(
            input !== reference,
            message ?? "Údaje musejí být shodné"
        ),
    });
}

class FieldValidationBuilder {
    validateInt(input: any, message?: Strings): FieldValidationBuilder {
        this.errors = FieldValidationBuilder.mergeErrors(
            this.errors,
            validateInt(input, message));
        return this;
    }

    validateIntGEZ(input: any, message?: Strings): FieldValidationBuilder {
        this.errors = FieldValidationBuilder.mergeErrors(
            this.errors,
            validateIntGEZ(input, message));
        return this;
    }

    validateIntGZ(input: any, message?: Strings): FieldValidationBuilder {
        this.errors = FieldValidationBuilder.mergeErrors(
            this.errors,
            validateIntGZ(input, message));
        return this;
    }

    validateNotEmpty(input: any, message?: Strings): FieldValidationBuilder {
        this.errors = FieldValidationBuilder.mergeErrors(
            this.errors,
            validateNotEmpty(input, message));
        return this;
    }

    validateNumber(input: any, message?: Strings): FieldValidationBuilder {
        this.errors = FieldValidationBuilder.mergeErrors(
            this.errors,
            validateNumber(input, message));
        return this;
    }

    validateNumberGEZ(input: any, message?: Strings): FieldValidationBuilder {
        this.errors = FieldValidationBuilder.mergeErrors(
            this.errors,
            validateNumberGEZ(input, message));
        return this;
    }

    validateNumberLE(input: any, than: any, message?: Strings): FieldValidationBuilder {
        this.errors = FieldValidationBuilder.mergeErrors(
            this.errors,
            validateNumberLE(input, message, than));
        return this;
    }

    validateChecked(input: boolean, message?: Strings): FieldValidationBuilder {
        this.errors = FieldValidationBuilder.mergeErrors(
            this.errors,
            validateChecked(input, message));
        return this;
    }

    validateEquals(input: any, reference: any, message?: Strings): FieldValidationBuilder {
        this.errors = FieldValidationBuilder.mergeErrors(
            this.errors,
            validateEquals(input, reference, message));
        return this;
    }

    validate(input: any, reference: any, validator: Validator<any>, message?: Strings): FieldValidationBuilder {
        this.errors = FieldValidationBuilder.mergeErrors(
            this.errors,
            validator(input, reference, message));
        return this;
    }

    isValid(): boolean {
        return isFieldErrorsValid(this.errors);
    }

    getResult(): FieldErrors {
        return this.errors;
    }

    public static mergeErrors(
        source1: FieldErrors,
        source2: FieldErrors): FieldErrors {
        let output: FieldErrors = source1;
        output.errors = addString(output.errors, source2.errors);
        return output;
    }

    private errors: FieldErrors = { ...NoFieldError };
}

const FieldValidator = (): FieldValidationBuilder => {
    return new FieldValidationBuilder();
}

class EntityValidationBuilder {
    storeFieldResult(field: string, result: FieldErrors) {
        this.errors[field] = result;
    }

    storeEntityResult(result: FieldErrors) {
        this.errors[WholeEntityError] = result;
    }

    isValid(): boolean {
        return isEntityErrorsValid(this.errors);
    }

    getResult(): EntityErrors {
        return this.errors;
    }

    private errors: EntityErrors = { ...NoEntityError };
}

const EntityValidator = (): EntityValidationBuilder => {
    return new EntityValidationBuilder();
}

export {
    DefaultValidator as AlwaysValid,
    EntityValidator,
    FieldValidator,
    validateChecked,
    validateEquals,
    validateInt,
    validateIntGEZ,
    validateIntGZ,
    validateNotEmpty,
    validateNumber,
    validateNumberGEZ,
    Validator
}
